import { Iconify, useIsTruncated } from '@mfw/components';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import type { Routes } from '../../../router';

import { type ListItemButtonProps, ListItemLink } from '../../List';
import { usePathname, useMatch } from '../../../router/hooks';
import { useBreadcrumbsContext } from '../../../context';
import { onSurface } from '../../../theme/onSurface';
import RouterLink from '../../RouterLink';

type IconProps = React.ComponentProps<typeof Iconify>['icon'];
export interface NavigationItemProps extends Omit<ListItemButtonProps, 'to'> {
  /**
   * Indicates to which route to move next
   */
  to?: '/' | Routes | string;
  'data-testid'?: string;
  /**
   * Shows right arrow icon and prevents router link
   */
  collapsable?: boolean;
  /**
   * Icon to display next to item button
   */
  icon?: IconProps;
  mini?: boolean;
  label: string;
  /**
   * Extra content rendered at the trailing edge of the row, e.g. a "soon"
   * pill or a hideable/visibility marker.
   */
  endAdornment?: React.ReactNode;
  /**
   * Selection comes from `selected` alone, never from the path. For rows that
   * share one path and differ only by `?tab=` (sidebar sub-items).
   */
  selectedByPropOnly?: boolean;
}

/**
 * `primary.main` measures 1.74:1 on a cream tenant (see
 * rebrand-safe-colors.spec) — the on-surface step is what keeps the selected
 * label and its accent bar legible on either ladder.
 */
const selectedItemSx = {
  fontWeight: 500,
  color: 'text.secondary',
  '&.Mui-selected': {
    color: onSurface.primary,
    fontWeight: 600,
    bgcolor: 'action.selected',
    boxShadow: `inset 3px 0 0 0 ${onSurface.primary}`,
  },
} as const;

const NavigationItem = React.forwardRef(
  (
    {
      children,
      className,
      collapsable,
      disabled,
      endAdornment,
      icon,
      label,
      mini,
      selected: propSelected,
      selectedByPropOnly,
      sx,
      to,
      ...otherProps
    }: NavigationItemProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { matches } = useBreadcrumbsContext();
    const [collapsed, setCollapsed] = React.useState(false);
    // `label` and `icon` are destructured above so they never reach the
    // anchor as DOM attributes (N3, bugz-6).
    const [labelRef, isLabelTruncated] = useIsTruncated(label);

    const pathname = usePathname();
    const match = useMatch(pathname);
    const toPathname = to?.split('?')[0];

    // If marked as selected in prop, or pathname full matches, or `to` matches
    // any ancestor route segment in the current match tree — not just the
    // deepest one, so a child route (e.g. a client's profile page under
    // `/clients`) still highlights the `/clients` nav item it descends from.
    // `isNavAncestor === false` opts a layout out of that ancestor check
    // (see organizationRoutes' `:organizationId`) when its pathname
    // coincides with an unrelated index route rather than a real section.
    const selected = selectedByPropOnly
      ? !!propSelected
      : propSelected ||
        match?.pathname === toPathname ||
        matches?.some((m) => m.pathname === toPathname && m.isNavAncestor);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (collapsable) {
        setCollapsed((state) => !state);
      }
      if (otherProps.onClick) otherProps.onClick(e);
    };
    return (
      <Box width="100%" sx={{ '& + &': { mt: 1 } }}>
        <ListItemLink
          {...otherProps}
          disabled={disabled}
          className={className}
          sx={sx ? [selectedItemSx, sx].flat() : selectedItemSx}
          selected={!collapsable && selected}
          onClick={handleClick}
          // In the mini bar the row is an icon and nothing else, so the label
          // moves onto the row itself: `aria-label` is the accessible name the
          // hidden text no longer provides, `title` the same hover reveal the
          // clipped expanded label gets (N3, docs/review/bugz-7.md).
          {...(mini ? { 'aria-label': label, title: label } : {})}
          {...(!collapsable && !disabled ? { component: RouterLink } : {})}
          {...(!collapsable && !disabled ? { to } : {})}
          ref={ref}
        >
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent={mini ? 'center' : 'space-between'}
          >
            {/* `minWidth: 0` lets the flex item shrink below its content so
                the label can actually clip — labels now carry the organization
                name ("<Org> Home"), up to 255 chars, into a 280px column. */}
            <Box display="flex" alignItems="center" minWidth={0}>
              {icon && (
                <Iconify sx={{ mr: mini ? 0 : 2 }} icon={icon} size="medium" />
              )}
              {!mini && (
                <Typography
                  component="span"
                  noWrap
                  ref={labelRef}
                  // Only a clipped label earns the native tooltip: on every
                  // row it duplicated ~60 fully visible labels (F5, bugz-5);
                  // on none it hid the one thing that told two clipped rows
                  // apart (G1, bugz-3).
                  title={isLabelTruncated ? label : undefined}
                  variant="inherit"
                >
                  {label}
                </Typography>
              )}
            </Box>
            {(collapsable || endAdornment) && !mini && (
              <Box display="flex" alignItems="center" gap={1}>
                {collapsable && (
                  <Iconify
                    icon={'mdi:keyboard-arrow-right'}
                    size="small"
                    sx={{
                      mr: 0,
                      display: 'inline-block',
                      transition: 'transform 0.5s',
                      transform: collapsed ? 'rotate(90deg)' : 'none',
                    }}
                  />
                )}
                {endAdornment}
              </Box>
            )}
          </Box>
        </ListItemLink>
        {children && collapsed && collapsable && (
          <Box pl={1} my={1}>
            {children}
          </Box>
        )}
      </Box>
    );
  },
);

NavigationItem.displayName = 'NavigationItem';

export default React.memo(NavigationItem);
